<template>
  <div class="mobile-shell">
    <!-- 顶部标题栏 -->
    <header class="m-header">
      <div class="m-brand">
        <img :src="logo" class="m-logo" alt="logo" />
        <div class="m-brand-text">
          <span class="m-brand-title">{{ currentTitle }}</span>
          <span class="m-brand-sub">行稳致远见未来</span>
        </div>
      </div>

      <button class="m-menu-btn" aria-label="打开菜单" @click="drawerVisible = true">
        <el-icon :size="20"><Expand /></el-icon>
      </button>
    </header>

    <!-- 内容区 -->
    <main class="m-main">
      <router-view />
    </main>

    <!-- 底部导航 -->
    <nav class="m-tabbar">
      <router-link
        v-for="tab in tabs"
        :key="tab.path"
        :to="tab.path"
        class="m-tab"
        :class="{ active: isActive(tab.path) }"
      >
        <el-icon :size="20"><component :is="tab.icon" /></el-icon>
        <span>{{ tab.label }}</span>
      </router-link>

      <button class="m-tab" type="button" @click="drawerVisible = true">
        <el-icon :size="20"><MoreFilled /></el-icon>
        <span>更多</span>
      </button>
    </nav>

    <!-- 侧边抽屉菜单 -->
    <el-drawer
      v-model="drawerVisible"
      direction="rtl"
      size="76%"
      :with-header="false"
    >
      <div class="m-drawer">
        <div class="drawer-head">
          <img :src="logo" class="drawer-logo" alt="logo" />
          <div class="drawer-brand">
            <div class="drawer-title">股票统计</div>
            <div class="drawer-sub">移动版</div>
          </div>
        </div>

        <el-menu
          :default-active="route.path"
          router
          class="drawer-menu"
          @select="drawerVisible = false"
        >
          <el-menu-item v-for="item in menus" :key="item.path" :index="item.path">
            <el-icon><component :is="item.icon" /></el-icon>
            <template #title>{{ item.label }}</template>
          </el-menu-item>

          <!-- 系统管理 -->
          <el-sub-menu v-if="systemMenus.length" index="system">
            <template #title>
              <el-icon><Setting /></el-icon>
              <span>系统管理</span>
            </template>

            <el-menu-item
              v-for="item in systemMenus"
              :key="item.path"
              :index="item.path"
            >
              <el-icon><component :is="item.icon" /></el-icon>
              <template #title>{{ item.label }}</template>
            </el-menu-item>
          </el-sub-menu>
        </el-menu>

        <div class="drawer-user">
          <el-avatar :size="34" :src="avatar" class="drawer-avatar">
            {{ userName.slice(0, 1) }}
          </el-avatar>
          <div class="drawer-user-meta">
            <span class="du-name">{{ userName }}</span>
            <span class="du-role">{{ roleText }}</span>
          </div>
          <el-button text size="small" class="logout-btn" @click="handleLogout">
            退出
          </el-button>
        </div>

        <div class="drawer-footer">
          <el-button plain size="small" @click="backToDesktop">切换到电脑版</el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  Histogram,
  Files,
  Operation,
  Document,
  TrendCharts,
  MagicStick,
  MoreFilled,
  Expand,
  Monitor,
  PieChart,
  Setting,
  User,
  Menu,
  Avatar,
  OfficeBuilding
} from '@element-plus/icons-vue'
import logo from '@/assets/xiaogouqianqian.jpeg'
import { setForceDesktop } from '@/utils/device'

const route = useRoute()
const router = useRouter()
const store = useStore()
const drawerVisible = ref(false)

const userName = computed(() => store.getters['user/userName'])
const avatar = computed(() => store.getters['user/avatar'])
const roleText = computed(() => {
  const roles = store.state.user.roles
  return roles.length ? roles.join(' / ') : '普通用户'
})

// 系统管理菜单（按权限过滤）
const systemMenus = computed(() => {
  const hasPermission = store.getters['user/hasPermission']

  const items = [
    { path: '/system/user', label: '用户管理', icon: User, permission: 'sys:user:list' },
    { path: '/system/role', label: '角色管理', icon: Avatar, permission: 'sys:role:list' },
    { path: '/system/menu', label: '菜单管理', icon: Menu, permission: 'sys:menu:list' },
    { path: '/system/dept', label: '部门管理', icon: OfficeBuilding, permission: 'sys:dept:list' }
  ]

  return items.filter(item => hasPermission(item.permission))
})

async function handleLogout() {
  try {
    await ElMessageBox.confirm('确定要退出登录吗？', '提示', {
      type: 'warning',
      confirmButtonText: '退出',
      cancelButtonText: '取消'
    })
  } catch (e) {
    return
  }

  drawerVisible.value = false
  await store.dispatch('user/logout')
  ElMessage.success('已退出登录')
  router.replace('/login')
}

const tabs = [
  { path: '/', label: '统计', icon: Histogram },
  { path: '/stcok-case', label: '案例', icon: Files },
  { path: '/stcok-action', label: '操作', icon: Operation },
  { path: '/stcok-compounding', label: '复利', icon: TrendCharts }
]

const menus = [
  { path: '/', label: '股票统计', icon: Histogram },
  { path: '/stcok-case', label: '股票案例', icon: Files },
  { path: '/stcok-action', label: '股票操作', icon: Operation },
  { path: '/stcok-article', label: '李剑文章', icon: Document },
  { path: '/stcok-compounding', label: '复利力量', icon: TrendCharts },
  { path: '/stcok-fortune', label: '算命复利', icon: MagicStick },
  { path: '/stcok-bar-chart', label: '柱状图示例', icon: PieChart },
  { path: '/stcok-screen', label: '股票大屏', icon: Monitor },
  { path: '/company-screen', label: '公司大屏', icon: Monitor }
]

const currentTitle = computed(() => {
  const all = [...menus, ...systemMenus.value]
  const hit = all.find(item => item.path === route.path)
  return hit?.label || route.meta?.title || '股票统计'
})

function isActive(path) {
  if (path === '/') return route.path === '/'
  return route.path.startsWith(path)
}

function backToDesktop() {
  setForceDesktop(true)
  window.location.reload()
}
</script>

<style lang="less" scoped>
.mobile-shell {
  display: flex;
  flex-direction: column;
  height: 100%;
  height: 100dvh;
  overflow: hidden;
  background: #f2f4f8;
}

// ===== 顶部栏 =====
.m-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  height: 56px;
  padding: 0 14px;
  padding-top: env(safe-area-inset-top);
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.16);
  color: #fff;
  z-index: 20;
}

.m-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  .m-logo {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    object-fit: cover;
    border: 1.5px solid rgba(255, 255, 255, 0.5);
    flex-shrink: 0;
  }

  .m-brand-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.2;

    .m-brand-title {
      font-size: 16px;
      font-weight: 700;
      letter-spacing: 0.5px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .m-brand-sub {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.65);
    }
  }
}

.m-menu-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s ease;

  &:active {
    background: rgba(255, 255, 255, 0.28);
  }
}

// ===== 内容区 =====
.m-main {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 12px 12px 16px;
}

// ===== 底部导航 =====
.m-tabbar {
  display: flex;
  flex-shrink: 0;
  align-items: stretch;
  height: 56px;
  padding-bottom: env(safe-area-inset-bottom);
  background: #fff;
  border-top: 1px solid #e8ecf3;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.05);
  z-index: 20;
}

.m-tab {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  border: none;
  background: transparent;
  color: #8a94a6;
  font-size: 11px;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;

  span {
    line-height: 1;
  }

  &.active {
    color: #2a5298;
    font-weight: 600;
  }

  &:active {
    background: #f5f7fb;
  }
}

// ===== 抽屉 =====
.m-drawer {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0 0 12px;
}

.drawer-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 18px 16px;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  color: #fff;

  .drawer-logo {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid rgba(255, 255, 255, 0.5);
  }

  .drawer-title {
    font-size: 16px;
    font-weight: 700;
  }

  .drawer-sub {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.65);
  }
}

.drawer-menu {
  flex: 1;
  border-right: none;
  overflow-y: auto;

  :deep(.el-menu-item) {
    height: 48px;
    line-height: 48px;
    font-size: 14px;

    &.is-active {
      background: #eef4ff;
      color: #2a5298;
      font-weight: 600;
      border-right: 3px solid #2a5298;
    }
  }
}

.drawer-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border-top: 1px solid #f0f2f5;

  .drawer-avatar {
    background: linear-gradient(135deg, #409eff, #2a5298);
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    flex-shrink: 0;
  }

  .drawer-user-meta {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    line-height: 1.3;

    .du-name {
      font-size: 13px;
      font-weight: 600;
      color: #1f2d3d;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .du-role {
      font-size: 11px;
      color: #9aa4b2;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .logout-btn {
    color: #f56c6c;
    flex-shrink: 0;
  }
}

.drawer-footer {
  padding: 12px 18px 0;
  border-top: 1px solid #f0f2f5;
  text-align: center;
}
</style>
